import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {RgbCurveSockets} from "#material/nodes/color/rgb-curve/definition"
import {rgbCurveDefinition} from "#material/nodes/color/rgb-curve/definition"
import {computeRgbCurveLut} from "#material/nodes/color/rgb-curve/helpers"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import {ThreeApplyLUTNode, createLutTypedArray, lutSize, threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"
import type {ColorNode, RgbCurvesNode} from "#material/cycles/types"
import type {Vec2} from "#material/types/vector"
import type {MaterialNodeParameters} from "#material/sockets/parameters"
import {cyclesColor} from "#material/cycles/helpers"

@registerMaterialGraphNode
export class RgbCurve extends MaterialNode<RgbCurveSockets> {
    public override get definition() {
        return rgbCurveDefinition
    }

    static controlPointsFromSettings(settings: MaterialNodeParameters<RgbCurveSockets>["settings"]) {
        const controlPoints: [Vec2[], Vec2[], Vec2[], Vec2[]] = [[], [], [], []]
        for (let curveIndex = 0; curveIndex < 4; curveIndex++) {
            const curveControlPoints = controlPoints[curveIndex]
            for (let i = 0; ; i++) {
                const curControlPoint = settings[`mappingCurves_${curveIndex}Points_${i}Location`]
                if (!curControlPoint) break
                curveControlPoints.push(curControlPoint)
            }
        }
        // we need at least two points for the Bezier spline computation
        if (controlPoints[0].length < 2) {
            return [
                [
                    {x: 0, y: 0},
                    {x: 1, y: 1},
                ],
                [
                    {x: 0, y: 0},
                    {x: 1, y: 1},
                ],
                [
                    {x: 0, y: 0},
                    {x: 1, y: 1},
                ],
                [
                    {x: 0, y: 0},
                    {x: 1, y: 1},
                ],
            ] as [Vec2[], Vec2[], Vec2[], Vec2[]]
        }

        return controlPoints
    }

    static settingsFromControlPoints(controlPoints: [Vec2[], Vec2[], Vec2[], Vec2[]], oldSettings?: MaterialNodeParameters<RgbCurveSockets>["settings"]) {
        const settings: MaterialNodeParameters<RgbCurveSockets>["settings"] = {}
        if (oldSettings) {
            for (let key of Object.keys(oldSettings)) {
                if (key.startsWith("mappingCurves_")) {
                    settings[key as keyof MaterialNodeParameters<RgbCurveSockets>["settings"]] = undefined
                }
            }
        }
        controlPoints.forEach((controlPointList, curveIndex) => {
            controlPointList.forEach((controlPoint, i) => {
                settings[`mappingCurves_${curveIndex}Points_${i}Location`] = controlPoint
            })
        })
        return settings
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<RgbCurveSockets>> => {
        if (this.parameters.ui?.disabled) {
            return {
                color:
                    (await evaluator.get(this.parameters.inputs.color))?.toColor() ??
                    threeConvert(this.parameters.settings.color, threeRGBColor) ??
                    threeRGBColor({
                        r: 0,
                        g: 0,
                        b: 0,
                    }),
            }
        }

        const {context} = evaluator
        const {logger} = context
        const parameters = this.parameters.settings
        const {color, fac} = await evaluator.getAll({color: this.parameters.inputs.color, fac: this.parameters.inputs.fac})

        const controlPoints = RgbCurve.controlPointsFromSettings(parameters)
        const colorValue = color?.toColor() ?? threeConvert(parameters.color, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})
        const facValue = fac?.toFloat() ?? threeConvert(parameters.fac, threeValue) ?? threeValue(1)
        const lut = computeRgbCurveLut(lutSize, controlPoints)
        const lutRgba = lut.map((x) => [...x, 1])

        const getTexture = () => {
            const {threePushLutTexture} = context
            if (!threePushLutTexture) {
                logger.warn("RGBCurve: No threePushLutTexture function provided, using local DataTexture instead")

                const floatType = context.threeDefaultFloatTextureType ?? THREE.HalfFloatType
                const texture = new THREE.DataTexture(createLutTypedArray(lutRgba.flat(), floatType), lutRgba.length, 1, THREE.RGBAFormat, floatType)
                texture.minFilter = THREE.LinearFilter
                texture.magFilter = THREE.LinearFilter
                texture.wrapS = THREE.ClampToEdgeWrapping
                texture.wrapT = THREE.ClampToEdgeWrapping
                texture.anisotropy = 1
                texture.colorSpace = THREE.LinearSRGBColorSpace
                texture.needsUpdate = true
                context.onThreeCreatedTexture?.(texture)
                return {texture, index: 0}
            } else {
                return threePushLutTexture(lutRgba)
            }
        }

        const {texture, index} = getTexture()

        return {color: TSL.color(new ThreeApplyLUTNode(TSL.vec4(colorValue, 1), texture, index, facValue).rgb)}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<RgbCurveSockets>> => {
        if (this.parameters.ui?.disabled) {
            const inputColor = await evaluator.get(this.parameters.inputs.color)
            if (inputColor) {
                return {color: inputColor}
            } else {
                const colorNode: ColorNode = {
                    type: "color",
                    inputs: {},
                    parameters: {value: cyclesColor(this.parameters.settings.color)},
                    resources: {},
                }
                return {color: [colorNode, "color"]}
            }
        }

        const controlPoints = RgbCurve.controlPointsFromSettings(this.parameters.settings)

        const rgbCurveNode: RgbCurvesNode = {
            type: "rgb_curves",
            inputs: await evaluator.getAll({fac: this.parameters.inputs.fac, value: this.parameters.inputs.color}),
            parameters: {
                fac: this.parameters.settings.fac,
                curves: computeRgbCurveLut(lutSize, controlPoints),
                value: cyclesColor(this.parameters.settings.color),
                extrapolate: undefined,
                max_x: undefined,
                min_x: undefined,
            },
            resources: {},
        }

        return {color: [rgbCurveNode, "value"]}
    }
}
